<template>
  <div v-if="!imgData.imageData.img.author" class="filler">
    <div class="filler--content">Oops!</div>
  </div>
  <figure :class="'fig_' + imgData.imageData.size" v-else>
    <source type="image/webp" :srcset="srcset_data.srcset1" />
    <source :media="srcset_data.srcset2media" :srcset="srcset_data.srcset2" />
    <img
      :srcset="srcset_data.srcset3"
      :alt="imgData.imageData.name.propValue"
      :class="'img_' + imgData.imageData.size"
    />

    <figcaption :class="'figcap_' + imgData.imageData.size">
      Image:
      <a
        :href="imgData.imageData.img.url"
        target="_blank"
        rel="noopener noreferrer nofollow noindex"
        :class="'img_alt_' + imgData.imageData.size"
        >{{ alt_data }}</a
      >

      by
      <a
        :href="aut_url_data"
        target="_blank"
        rel="noopener noreferrer nofollow noindex"
        class="alink"
        >{{ imgData.imageData.img.author }}</a
      >, marked with
      <a
        :href="license_data"
        target="_blank"
        rel="noopener noreferrer nofollow noindex"
        class="alink"
        >{{ imgData.imageData.img.license }}</a
      >
    </figcaption>
  </figure>
</template>

<script setup>
import { defineProps, computed } from "vue";

const imgData = defineProps({ imageData: Object });

const alt_data = computed(() => {
  if (!imgData.imageData.img.alt) {
    return imgData.imageData.name.propValue;
  }
  return imgData.imageData.img.alt;
});

const aut_url_data = computed(() => {
  const regex = /Smithsonian/g;
  if (regex.test(imgData.imageData.img.author)) {
    return "https://naturalhistory.si.edu";
  }
  return imgData.imageData.img.author_url;
});
const license_data = computed(() => {
  const digits = imgData.imageData.img.license.match(/\d.\d/);
  if (imgData.imageData.img.license.match(/1.0/g)) {
    return "https://creativecommons.org/publicdomain/zero/1.0/";
  }
  if (imgData.imageData.img.license.match(/SA/g)) {
    return `https://creativecommons.org/licenses/by-sa/${digits}/`;
  }

  return `https://creativecommons.org/licenses/by/${digits}/`;
});

const srcset_data = computed(() => {
  if (imgData.imageData.size === "mini") {
    return {
      srcset1: imgData.imageData.source + "_510w.webp",
      srcset2media: "(max-width: 425px)",
      srcset2: imgData.imageData.source + "_252w.webp",
      srcset3: imgData.imageData.source + "_510w.jpg",
    };
  }

  if (imgData.imageData.size === "medium") {
    return {
      srcset1: imgData.imageData.source + "_785w.webp",
      srcset2media: "(max-width: 950px)",
      srcset2: imgData.imageData.source + "_510w.webp",
      srcset3: imgData.imageData.source + "_785w.jpg",
    };
  }

  if (
    imgData.imageData.size === "miniMineral" ||
    imgData.imageData.size === "minFilterCard"
  ) {
    return {
      srcset1: imgData.imageData.source + "_small-1x.jpg",
      srcset2media: "(max-width: 425px)",
      srcset2: imgData.imageData.source + "_medium.webp",
      srcset3: imgData.imageData.source + "_medium.jpg",
    };
  }
  if (imgData.imageData.size === "mediumMineral") {
    return {
      srcset1: imgData.imageData.source + "_medium.webp",
      srcset2media: "(max-width: 425px)",
      srcset2: imgData.imageData.source + "_medium.jpg",
      srcset3: imgData.imageData.source + "_large.webp",
    };
  }

  if (imgData.imageData.size === "miniMineralBCard") {
    return {
      srcset1: imgData.imageData.source + "_medium.webp",
      srcset2media: "(max-width: 425px)",
      srcset2: imgData.imageData.source + "_medium-1x.jpg",
      srcset3: "./img/minerals/" + imgData.imageData.idName + "_medium.jpg",
    };
  }
  return {};
});
</script>

<style scoped>
.filler {
  margin: 3rem;
  border: 0.5rem dotted var(--color-grey-4);
  border-radius: 12px;

  display: flex;
  justify-content: center;
  align-items: center;
}
.filler--content {
  background-color: var(--color-primary-4);
  font-size: 1.8rem;
  padding: 1rem;
  font-weight: 700;
}
figure {
  text-align: center;
  overflow: hidden;
}
/* 421px*/
@media only screen and (max-width: 26.3em) {
  figure {
    margin-top: 1rem;
    padding: 1rem;
  }
}

img {
  object-fit: contain;
}

/* MINI: ROCKS */

.img_mini,
.img_miniMineralBCard,
.img_minFilterCard {
  width: 90%;
  height: 85%;
  object-fit: contain;
}
/*900px */
@media only screen and (max-width: 56.25em) {
  /* 900px/16 */
  .fig_mini,
  .figcap__miniMineralBCard,
  .figcap_minFilterCard {
    max-width: 160px;
    max-height: 150px;
  }
  .img_mini,
  .img_miniMineralBCard {
    height: 80%;
  }
}
.img_miniMineral {
  /* width: 90%;
  height: 85%; */
  width: 150px;
  height: 100px;
  object-fit: contain;
}

/* MEDIUM */
.img_medium,
.img_mediumMineral {
  max-width: 80%;
  max-height: 40rem;
  /* object-fit: contain; */
}
@media only screen and (max-width: 45em) {
  .img_medium,
  .img_mediumMineral {
    max-height: 30rem;
  }
}

@media only screen and (max-width: 26.56em) {
  .img_medium,
  .img_mediumMineral {
    max-width: 70%;
  }
}

.figcap_medium,
.figcap_mediumMineral {
  text-align: center;
  padding: 0 4rem;
  a:hover {
    color: var(--color-primary-3);
  }
}
@media only screen and (max-width: 45em) {
  .figcap_medium,
  .figcap_mediumMineral {
    max-width: 50%;
    margin: 0 auto;
    padding: 0;
  }
}
@media only screen and (max-width: 37.5em) {
  .figcap_medium,
  .figcap_mediumMineral {
    max-width: 65%;
  }
}
@media only screen and (max-width: 26.56em) {
  .figcap_medium,
  .figcap_mediumMineral {
    max-width: 75%;
  }
}

.figcap_minFilterCard,
.figcap__miniMineralBCard {
  font-size: 1rem;
}
.figcap_mini,
.figcap__miniMineralBCard,
.figcap_minFilterCard {
  a {
    pointer-events: none;
  }
}
@media only screen and (max-width: 75em) {
  .figcap_mini,
  .figcap__miniMineralBCard,
  .figcap_minFilterCard {
    font-size: 0.8rem;
  }
}

.img_alt_mini,
.img_alt_miniMineralBCard,
.img_alt_minFilterCard {
  text-transform: capitalize;

  display: inline-block;
  max-width: 20rem;
  vertical-align: bottom;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.img_alt_medium,
.img_alt_mediumMineral {
  text-transform: capitalize;
}
.img_alt_medium:hover,
.img_alt_mediumMineral:hover {
  color: chocolate;
}

.alink:hover {
  color: chocolate;
}

figcaption {
  padding: 0.2rem;
}
</style>
